import React, { useState, useEffect } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { Plus, Calendar, Search, Upload, Download, Edit2, Trash2, CalendarDays, Clock } from 'lucide-react';
import { formatDate } from '@/lib/utils';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const currentYear = new Date().getFullYear();
const yearOptions = [currentYear - 1, currentYear, currentYear + 1, currentYear + 2];

const initialFormData = {
  date: '',
  name: '',
  description: '',
  day_type: 'full_day',
  half_day_time: ''
};

export default function HolidayMasterPage() {
  const { getAuthHeader } = useAuth();
  const [holidays, setHolidays] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterYear, setFilterYear] = useState(currentYear.toString());
  const [open, setOpen] = useState(false);
  const [editHoliday, setEditHoliday] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [uploading, setUploading] = useState(false);

  const fetchHolidays = async () => {
    try {
      const params = new URLSearchParams();
      if (filterYear && filterYear !== 'all') params.append('year', filterYear);
      
      const response = await axios.get(`${API_URL}/holidays?${params}`, { headers: getAuthHeader() });
      setHolidays(response.data);
    } catch (error) {
      toast.error('Failed to fetch holidays');
    }
  };

  useEffect(() => {
    fetchHolidays();
  }, [filterYear]);

  const filteredHolidays = holidays.filter(h => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      h.name?.toLowerCase().includes(term) ||
      h.date?.includes(term) ||
      h.description?.toLowerCase().includes(term)
    );
  });

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      if (editHoliday) {
        await axios.patch(`${API_URL}/holidays/${editHoliday.id}`, formData, { headers: getAuthHeader() });
        toast.success('Holiday updated successfully');
      } else {
        await axios.post(`${API_URL}/holidays`, formData, { headers: getAuthHeader() });
        toast.success('Holiday created successfully');
      }
      setOpen(false);
      setEditHoliday(null);
      setFormData(initialFormData);
      fetchHolidays();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save holiday');
    }
  };

  const handleEdit = (holiday) => {
    setEditHoliday(holiday);
    setFormData({
      date: holiday.date,
      name: holiday.name,
      description: holiday.description || '',
      day_type: holiday.day_type || 'full_day',
      half_day_time: holiday.half_day_time || ''
    });
    setOpen(true);
  };

  const handleDelete = async (holidayId) => {
    if (!confirm('Are you sure you want to delete this holiday?')) return;
    try {
      await axios.delete(`${API_URL}/holidays/${holidayId}`, { headers: getAuthHeader() });
      toast.success('Holiday deleted');
      fetchHolidays();
    } catch (error) {
      toast.error('Failed to delete holiday');
    }
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const uploadData = new FormData();
    uploadData.append('file', file);

    try {
      const response = await axios.post(`${API_URL}/holidays/bulk-upload`, uploadData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`Created ${response.data.created} holidays, skipped ${response.data.skipped} duplicates`);
      if (response.data.errors?.length > 0) {
        toast.error(`${response.data.errors.length} errors occurred`);
        console.log('Upload errors:', response.data.errors);
      }
      fetchHolidays();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const downloadTemplate = () => {
    const headers = ['DATE', 'NAME', 'DESCRIPTION', 'DAY_TYPE', 'HALF_DAY_TIME'];
    const csvContent = headers.join(',') + '\n2025-01-26,REPUBLIC DAY,National Holiday,full_day,\n2025-08-15,INDEPENDENCE DAY (MORNING),Morning holiday,first_half,13:30\n2025-10-02,GANDHI JAYANTI,National Holiday,full_day,';
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'holiday_template.csv';
    link.click();
  };

  const getDayOfWeek = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { weekday: 'long' });
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <CalendarDays className="w-6 h-6" />
              Holiday Master
            </h1>
            <p className="text-muted-foreground">Manage company-wide holidays for TAT calculation</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={downloadTemplate}>
              <Download className="w-4 h-4 mr-2" />
              Template
            </Button>
            <label>
              <Button variant="outline" disabled={uploading} asChild>
                <span>
                  <Upload className="w-4 h-4 mr-2" />
                  {uploading ? 'Uploading...' : 'Bulk Upload'}
                </span>
              </Button>
              <input type="file" accept=".xlsx,.xls,.csv" onChange={handleBulkUpload} className="hidden" />
            </label>
            <Dialog open={open} onOpenChange={(isOpen) => {
              setOpen(isOpen);
              if (!isOpen) {
                setEditHoliday(null);
                setFormData(initialFormData);
              }
            }}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Holiday
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>{editHoliday ? 'Edit Holiday' : 'Add New Holiday'}</DialogTitle>
                  <DialogDescription>
                    {editHoliday ? 'Update holiday information' : 'Add a new company holiday'}
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreate}>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label>Date *</Label>
                      <Input
                        type="date"
                        value={formData.date}
                        onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Holiday Name *</Label>
                      <Input
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value.toUpperCase() })}
                        placeholder="HOLIDAY NAME"
                        required
                        style={{ textTransform: 'uppercase' }}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Description</Label>
                      <Textarea
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder="Optional description..."
                        rows={2}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Day Type</Label>
                      <Select
                        value={formData.day_type}
                        onValueChange={(val) => setFormData({ ...formData, day_type: val, half_day_time: val === 'full_day' ? '' : formData.half_day_time })}
                      >
                        <SelectTrigger data-testid="day-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="full_day">Full Day</SelectItem>
                          <SelectItem value="first_half">First Half (Morning Off)</SelectItem>
                          <SelectItem value="second_half">Second Half (Afternoon Off)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {formData.day_type !== 'full_day' && (
                      <div className="space-y-2">
                        <Label className="flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5" />
                          {formData.day_type === 'first_half' ? 'Holiday until (employees work after this time)' : 'Holiday from (employees work before this time)'}
                        </Label>
                        <Input
                          type="time"
                          value={formData.half_day_time}
                          onChange={(e) => setFormData({ ...formData, half_day_time: e.target.value })}
                          required
                          data-testid="half-day-time-input"
                        />
                      </div>
                    )}
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                    <Button type="submit">{editHoliday ? 'Update Holiday' : 'Add Holiday'}</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-4 items-center">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search holidays..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={filterYear} onValueChange={setFilterYear}>
            <SelectTrigger className="w-36">
              <SelectValue placeholder="Year" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Years</SelectItem>
              {yearOptions.map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Total Holidays</p>
              <p className="text-2xl font-bold">{holidays.length}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">This Year ({currentYear})</p>
              <p className="text-2xl font-bold text-blue-600">{holidays.filter(h => h.year === currentYear).length}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Next Year ({currentYear + 1})</p>
              <p className="text-2xl font-bold text-green-600">{holidays.filter(h => h.year === currentYear + 1).length}</p>
            </CardContent>
          </Card>
        </div>

        {/* Table */}
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead>Date</TableHead>
                <TableHead>Day</TableHead>
                <TableHead>Holiday Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredHolidays.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12">
                    <CalendarDays className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                    <p className="text-muted-foreground">No holidays found</p>
                    <p className="text-sm text-muted-foreground">Add holidays or upload a CSV/Excel file</p>
                  </TableCell>
                </TableRow>
              ) : (
                filteredHolidays.map((holiday) => (
                  <TableRow key={holiday.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-muted-foreground" />
                        <span className="font-mono">{formatDate(holiday.date)}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{getDayOfWeek(holiday.date)}</TableCell>
                    <TableCell className="font-medium">{holiday.name}</TableCell>
                    <TableCell>
                      {(!holiday.day_type || holiday.day_type === 'full_day') ? (
                        <span className="text-xs px-2 py-0.5 rounded bg-red-100 text-red-700">Full Day</span>
                      ) : holiday.day_type === 'first_half' ? (
                        <span className="text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-700">
                          First Half {holiday.half_day_time ? `(until ${holiday.half_day_time})` : ''}
                        </span>
                      ) : (
                        <span className="text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-700">
                          Second Half {holiday.half_day_time ? `(from ${holiday.half_day_time})` : ''}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground max-w-xs truncate">{holiday.description || '-'}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => handleEdit(holiday)}>
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDelete(holiday.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Info Note */}
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> Full-day holidays are excluded from TAT calculations and checklist generation.
              Half-day holidays (First Half / Second Half) count as working days — only the working portion is used for time-based TAT calculations.
              Sundays are automatically treated as non-working days.
            </p>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
